import { AxiosInstance } from 'axios'

export type PrintingTemplateType = 'receipt' | 'report'

export type PrintingTemplate = {
  id: number
  name: string
  physicianId: number
  pharmaceuticalId: number
  header: string | null
  // headerHeight?: number | null
  footer: string | null
  // footerHeight?: number | null
  choices?: { footer: Record<string, number>; header: Record<string, number> }
  clinicLogo?: string
  default: boolean
  type: PrintingTemplateType
}

export function printingTemplateEndpoints(apiClient: AxiosInstance) {
  return {
    async list(query: { type?: 'receipt' | 'report' | null; withDefaults?: boolean }): Promise<PrintingTemplate[]> {
      return apiClient.get('list-printing-templates', {
        params: query,
      })
    },

    async generatePreview(data: {
      type: string
      name: string
      clinicLogo?: File
      header: string[]
      footer: string[]
      choices: Record<string, Record<string, number>>
    }): Promise<Blob> {
      const formData = new FormData()
      formData.append('type', data.type)
      formData.append('name', data.name)
      if (data.clinicLogo) {
        formData.append('clinicLogo', data.clinicLogo)
      }
      formData.append('header', JSON.stringify(data.header))
      formData.append('footer', JSON.stringify(data.footer))
      formData.append('choices', JSON.stringify(data.choices))

      return apiClient.post('generate-printing-template-preview', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob',
      })
    },

    async getPreview(templateId: number): Promise<Blob> {
      return apiClient.get(`get-printing-template-preview/${templateId}`, {
        responseType: 'blob',
      })
    },

    async print(data: {
      templateId: number
      patientId: number
      text: string
      extraText?: string | null
      withSignature?: boolean
      withUnsignedFooter?: boolean
      documentId?: number
    }): Promise<Blob> {
      return apiClient.post('print-printing-template', data, {
        responseType: 'blob',
      })
    },

    async create(data: {
      type: string
      name: string
      clinicLogo?: File
      header: string[]
      footer: string[]
      choices: Record<string, Record<string, number>>
    }): Promise<PrintingTemplate> {
      const formData = new FormData()
      formData.append('type', data.type)
      formData.append('name', data.name)
      if (data.clinicLogo) {
        formData.append('clinicLogo', data.clinicLogo)
      }
      formData.append('header', JSON.stringify(data.header))
      formData.append('footer', JSON.stringify(data.footer))
      formData.append('choices', JSON.stringify(data.choices))

      return apiClient.post('create-printing-template', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    },

    async edit(
      templateId: number,
      data: {
        type: string
        name: string
        clinicLogo?: File
        header: string[]
        footer: string[]
        choices: Record<string, Record<string, number>>
      },
    ): Promise<PrintingTemplate> {
      const formData = new FormData()
      formData.append('type', data.type)
      formData.append('name', data.name)
      if (data.clinicLogo) {
        formData.append('clinicLogo', data.clinicLogo)
      }
      formData.append('header', JSON.stringify(data.header))
      formData.append('footer', JSON.stringify(data.footer))
      formData.append('choices', JSON.stringify(data.choices))

      return apiClient.put(`edit-printing-template/${templateId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    },

    async delete(templateId: number): Promise<void> {
      return apiClient.delete(`delete-printing-template/${templateId}`)
    },

    async restore(templateId: number): Promise<void> {
      return apiClient.patch(`restore-printing-template/${templateId}`)
    },
  }
}
